import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { useCallback, useMemo, useState, useEffect } from "react";
import NoResultsTemplate from "../../common/NoResultsTemplate.tsx";
import styles from "./SampleResults.module.scss";
import PaddedDotLoader from "../../common/PaddedDotLoader.tsx";
import { getSearchQuery } from "../../query.ts";
import useSampleTitleResults from "../results/useSampleTitleResults.ts";
import SearchSampleTitleResults from "./SearchSampleTitleResults.tsx";
import SampleFilters, { FilterValue } from "../filter/SampleFilter.tsx";
import { exhaustiveArray } from "common/utils/typeUtils";
import { logEvent } from "../../../../../common/api/monitoring/index.ts";
import useSingleToast from "../../../../root/core-ui/toast/useSingleToast.ts";
import Toast from "../../../../../common/core-ui/Toast.tsx";
import BaseLinkOrButton from "../../../../../common/core-ui/BaseLinkOrButton.tsx";
import { useSampleSearchLog } from "../useSampleSearchLog.ts";

// Type-safe array that ensures all FilterValue types are included
// If a new FilterValue is added, TypeScript will require it to be added here
const validFilters = exhaustiveArray<FilterValue>()([
  "available_to_sample",
  "coach_me_included",
  "total_access",
  "accessibility_claims",
]);

function LoadingState() {
  return (
    <>
      <PaddedDotLoader aria-hidden={true} />
      <div className={styles.loadingTitle}>
        <FormattedMessage defaultMessage="Loading Sample Results" />
      </div>
      <div className={styles.loadingMessage}>
        <FormattedMessage defaultMessage="This may take a few moments" />
      </div>
    </>
  );
}

export default function SampleResults() {
  const [urlParams, setUrlParams] = useSearchParams();
  const query = getSearchQuery(urlParams);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [showToast] = useSingleToast();
  const log = useSampleSearchLog();

  // Get filters directly from URL - single source of truth
  const filters = useMemo((): FilterValue[] => {
    const filterParam = urlParams.get("filters");
    if (!filterParam) {
      return [];
    }

    const filterValues = filterParam.split(",") as FilterValue[];
    return filterValues.filter((f) => validFilters.includes(f));
  }, [urlParams]);

  // Update URL when filters change
  const handleFiltersChange = useCallback(
    (newFilters: FilterValue[]) => {
      const newParams = new URLSearchParams(urlParams);

      if (newFilters.length > 0) {
        newParams.set("filters", newFilters.join(","));
      } else {
        newParams.delete("filters");
      }

      setUrlParams(newParams, { replace: true });
    },
    [urlParams, setUrlParams]
  );

  const sampleResults = useSampleTitleResults(filters);

  const logFiltersString = useMemo(
    () =>
      filters
        .map((filter) =>
          filter === "total_access" ? "day_one_access_pricing" : filter
        )
        .join(","),
    [filters]
  );

  // Track search queries
  useEffect(() => {
    if (query) {
      log("sample_search_query", {
        query,
        ...(filters.length > 0 && { filters: logFiltersString }),
      });
    }
    // Only track when query or filter values change, not when log function changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, logFiltersString]);

  // Track when initial load is complete
  useEffect(() => {
    if (sampleResults.status !== "loading" && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
    }
  }, [sampleResults.status, hasInitiallyLoaded]);

  // Log to New Relic when error or no results occur (page 1 only)
  useEffect(() => {
    if (sampleResults.status === "error") {
      logEvent("sample_search_server_error", {
        query: query || "",
        status: sampleResults.status,
        page: 1, // Error status only shows for page 1 failures
      });
    } else if (sampleResults.status === "empty") {
      logEvent("sample_search_no_result_found", {
        query: query || "",
        status: sampleResults.status,
      });
    }
  }, [sampleResults.status, query]);

  // Handle "Let us know" click - only show toast, don't log again
  const handleLetUsKnow = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      // Show confirmation toast
      showToast(
        <Toast
          type="success"
          title={
            <FormattedMessage defaultMessage="Thanks for letting us know!" />
          }
        >
          <FormattedMessage defaultMessage="This will help us improve as we continue to add more titles to sample." />
        </Toast>
      );
    },
    [showToast]
  );

  function getMainContent() {
    if (sampleResults.status === "loading") {
      return <LoadingState />;
    }

    if (sampleResults.status === "empty") {
      return (
        <NoResultsTemplate
          className={styles.noResults}
          title={
            <FormattedMessage
              defaultMessage="We couldn’t find a match for “{query}”"
              values={{ query }}
            />
          }
          message={
            <FormattedMessage defaultMessage="Double-check your spelling, or try using a different search term" />
          }
        />
      );
    } else if (sampleResults.status === "error") {
      return (
        <NoResultsTemplate
          className={styles.noResults}
          title={<FormattedMessage defaultMessage="Something went wrong." />}
          message={
            <FormattedMessage
              defaultMessage="Not what you expected? <button>Let us know</button>."
              values={{
                button: (chunks) => (
                  <BaseLinkOrButton
                    onClick={handleLetUsKnow}
                    className="vst-link"
                  >
                    {chunks}
                  </BaseLinkOrButton>
                ),
              }}
            />
          }
        />
      );
    }

    return <SearchSampleTitleResults sampleResults={sampleResults} />;
  }

  // Show filters after initial load completes, and hide only when no results
  const showFilters =
    hasInitiallyLoaded &&
    sampleResults.status !== "empty" &&
    sampleResults.status !== "error";

  return (
    <div className={styles.sampleResultsContainer}>
      {showFilters && (
        <>
          <FormattedMessage defaultMessage="All Title Results" tagName="h2" />
          <SampleFilters
            value={filters}
            onChange={handleFiltersChange}
            onClear={() => handleFiltersChange([])}
            isActionBlocked={sampleResults.isFetching}
            programName={sampleResults.programName}
          />
        </>
      )}

      {getMainContent()}
    </div>
  );
}
